import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, iif, lastValueFrom, of } from 'rxjs';
import { Training } from './training.model';
import { FirebaseApiProvider } from 'libs/api/providers/firebase-api.provider';
import { filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { UnsubscriberService } from 'libs/unsubcriber/unsubscriber.service';
import { UserService } from 'libs/user/user.service';
import { User } from 'libs/user/user.model';
import { WhereQuery } from 'libs/api/api.provider';
import { construireEffectifFige } from './effectif';
import { Affiliation, AffiliationRole } from 'libs/affiliation/affiliation.model';
import { AffiliationService } from 'libs/affiliation/affiliation.service';

@Injectable({
  providedIn: 'root',
})
export class TrainingService {
  private isListeningToTrainingIds: string[] = [];
  private isListeningToAllTrainings = false;
  private trainingsSubject: BehaviorSubject<Training[]> = new BehaviorSubject([]);
  trainings$: Observable<Training[]> = this.trainingsSubject.asObservable();

  private currentTrainingIdSubject: BehaviorSubject<string> = new BehaviorSubject(null);
  currentTrainingId$: Observable<string> = this.currentTrainingIdSubject.asObservable();

  constructor(
    private apiService: FirebaseApiProvider,
    private userService: UserService,
    private unsubscriberService: UnsubscriberService,
    private affiliationService: AffiliationService,
  ) {}

  getAllTrainings(trainingCenterId?: string): Observable<Training[]> {
    if (!this.isListeningToAllTrainings) {
      console.log('getUserAllTrainings API CALL LISTENING');
      this.listenToAllTrainingChanges(trainingCenterId);
    } else {
      console.log('getUserAllTrainings FROM CACHE');
    }

    return this.trainings$;
  }

  getUserActiveTrainings(trainingIds: string[]): Observable<Training[]> {
    if (
      JSON.stringify(trainingIds) !== JSON.stringify(this.isListeningToTrainingIds) &&
      !this.isListeningToAllTrainings
    ) {
      console.log('getUserActiveTrainings API CALL LISTENING:', trainingIds);
      this.listenToTrainingChanges(trainingIds);
    } else {
      console.log('getUserActiveTrainings FROM CACHE', trainingIds);
    }

    return this.trainings$.pipe(
      filter<Training[]>((trainings) => trainings.length > 0),
      map((trainings) =>
        trainings.filter((training) => trainingIds.includes(training.id) && training.isActive === true),
      ),
    );
  }

  getUserNonArchivedTrainings(trainingIds: string[]): Observable<Training[]> {
    if (
      JSON.stringify(trainingIds) !== JSON.stringify(this.isListeningToTrainingIds) &&
      !this.isListeningToAllTrainings
    ) {
      console.log('getUserNonArchivedTrainings API CALL LISTENING:', trainingIds);
      this.listenToTrainingChanges(trainingIds);
    } else {
      console.log('getUserNonArchivedTrainings FROM CACHE', trainingIds);
    }

    return this.trainings$.pipe(
      filter<Training[]>((trainings) => trainings.length > 0),
      map((trainings) =>
        trainings.filter((training) => trainingIds.includes(training.id) && training.isArchived === false),
      ),
    );
  }

  private listenToAllTrainingChanges(trainingCenterId?: string) {
    this.isListeningToAllTrainings = true;

    const whereQ: WhereQuery[] = [];
    if (trainingCenterId) {
      whereQ.push({
        fieldPath: 'trainingCenterId',
        condition: '==',
        value: trainingCenterId,
      });
    }

    const trainingsSubscriber = this.apiService
      .listenToChanges<Training>('trainings', Training.fromObject, whereQ, 'title')
      .pipe(
        finalize(() => {
          this.isListeningToAllTrainings = false;
          console.log('listenToAllTrainingChanges finalized.');
        }),
      )
      .subscribe((trainings) => {
        console.log('listenToAllTrainingChanges NEXT:', trainings);
        this.trainingsSubject.next(trainings);
      });
    this.unsubscriberService.add('TrainingService:listenToAllTrainingChanges', trainingsSubscriber);
  }

  private listenToTrainingChanges(trainingIds: string[]) {
    if (this.trainingsSubject.getValue()) {
      this.trainingsSubject.next([]);
    }
    this.unsubscriberService.unsubscribe('TrainingService:listenToTrainingChanges');
    this.isListeningToTrainingIds = trainingIds;

    const trainingsSubscriber = this.apiService
      .listenToChanges<Training>('trainings', Training.fromObject, [
        {
          fieldPath: 'id',
          condition: 'in',
          value: trainingIds,
        },
        {
          fieldPath: 'isActive',
          condition: '==',
          value: true,
        },
      ])
      .pipe(
        finalize(() => {
          this.isListeningToTrainingIds = [];
          console.log('listenToTrainingChanges finalized.');
        }),
      )
      .subscribe((trainings) => {
        console.log('listenToTrainingChanges NEXT:', trainings);
        this.trainingsSubject.next(trainings);
      });
    this.unsubscriberService.add('TrainingService:listenToTrainingChanges', trainingsSubscriber);
  }

  getUserTrainingById(trainingId: string): Observable<Training> {
    console.log('getUserTrainingById trainingId:', trainingId);
    if (!this.isListeningToTrainingIds.includes(trainingId) && !this.isListeningToAllTrainings) {
      this.getUserActiveTrainings([trainingId]);
    }

    const training$ = this.trainings$.pipe(
      filter<Training[]>((trainings) => trainings.length > 0),
      map((trainings) => trainings.find((training) => training.id === trainingId)),
    );

    return this.withTrainingUsers(training$);
  }

  withTrainingUsers(training$: Observable<Training>): Observable<Training> {
    return training$.pipe(
      switchMap((training) => {
        // if (!training.apprentices.length) {
        //   return combineLatest([training$, of([])]);
        // }

        const { apprenticeIds, tutorIds } = training.apprentices?.reduce(
          (ids, apprentice) => {
            ids.apprenticeIds.push(apprentice.id);
            apprentice.tutors?.forEach((tutorId) => ids.tutorIds.push(tutorId));
            return ids;
          },
          { apprenticeIds: [], tutorIds: [] },
        );
        const trainingUserIds = Array.from(new Set([...apprenticeIds, ...tutorIds, ...training.managers]));

        if (trainingUserIds.length > 0) {
          const trainingUsers$ = this.userService.getUsers(trainingUserIds).pipe(
            map((users) => users.filter((user) => trainingUserIds.includes(user.id))),
            filter<User[]>((users) => users.length > 0 && users.length === trainingUserIds.length),
          );

          return combineLatest([training$, trainingUsers$]);
        }
        return combineLatest([training$, of([])]);
      }),
      map(([training, users]) => {
        if (users.length > 0) {
          // Classement par rôle TENU DANS CETTE FORMATION, pas par user.role
          // (rôle légataire global, projeté par priorité — apprenti actif
          // d'abord — sur l'ensemble des affiliations de l'identité). Une
          // identité multi-rôle peut avoir user.role === 'apprentice' tout en
          // étant tuteur ICI : l'indexer par user.role la classait alors dans
          // training.users.apprentices, laissant training.users.tutors[id]
          // undefined et faisant planter le template qui le déréférence.
          const { apprenticeIds, tutorIds, managerIds } = Training.membershipIds(training);
          users.forEach((user) => {
            const roleInTraining = apprenticeIds.has(user.id)
              ? 'apprentice'
              : tutorIds.has(user.id)
                ? 'tutor'
                : managerIds.has(user.id)
                  ? 'manager'
                  : user.role;
            training.users[`${roleInTraining}s`][user.id] = user;
          });
          if (training.isArchived === true) {
            // L'effectif d'une promotion passee est la liste que porte la
            // formation, et non le role courant de ses membres. La ligne
            // precedente remplacait l'effectif par les seuls `alumni` :
            // 75 personnes sur 316 etaient invisibles en production le
            // 03/09/2026, et une de plus a chaque reinscription — un stagiaire
            // qui reprend ses etudes disparaissait de la promotion qu'il avait
            // pourtant suivie.
            training.users.apprentices = construireEffectifFige(training.apprentices, users);
          } else {
            training.apprentices?.forEach((apprentice) => {
              if (training.users.apprentices[apprentice.id]) {
                training.users.apprentices[apprentice.id].tutors = apprentice.tutors?.map(
                  (tutorId: string) => training.users.tutors[tutorId],
                );
              }
            });
          }
        }

        return training;
      }),
    );
  }

  /**
   * S3-A : `relationRole` est désormais explicite — le rôle de la relation
   * qu'on détache, pas déduit de `user.role` (global, potentiellement
   * différent du rôle que cette personne tient dans CETTE formation dès
   * qu'un compte cumule des rôles différents selon la formation).
   */
  async unlinkUserFromTraining(
    currentTraining: Training,
    user: User,
    relationRole: AffiliationRole,
    linkedUser?: User,
    update = true,
  ): Promise<Training> {
    try {
      let tutorStillLinked = false;

      switch (relationRole) {
        case 'manager':
          const managerIndex = currentTraining.managers.findIndex((managerId) => managerId === user.id);
          if (managerIndex !== -1) {
            currentTraining.managers.splice(managerIndex, 1);
          }
          break;
        case 'apprentice':
          const apprenticeIndex = currentTraining.apprentices.findIndex((apprentice) => apprentice.id === user.id);
          if (apprenticeIndex !== -1) {
            currentTraining.apprentices.splice(apprenticeIndex, 1);
          }
          break;
        case 'tutor':
          if (linkedUser) {
            const tutorApprenticeIndex = currentTraining.apprentices.findIndex(
              (apprentice) => apprentice.id == linkedUser.id,
            );
            if (tutorApprenticeIndex !== -1) {
              const tutorIndex = currentTraining.apprentices[tutorApprenticeIndex].tutors?.findIndex(
                (tutorId) => tutorId === user.id,
              );
              if (tutorIndex !== -1) {
                currentTraining.apprentices[tutorApprenticeIndex].tutors.splice(tutorIndex, 1);
              }
            }
          }
          // Une formation peut avoir plusieurs stagiaires tutorés par la même
          // personne : l'affiliation « tuteur dans cette formation » ne se
          // termine que si plus aucun stagiaire ne la liste.
          tutorStillLinked = currentTraining.apprentices.some((apprentice) => apprentice.tutors?.includes(user.id));
      }

      if (update) {
        // F-04 (audit du 28/10/2026) : attendue — sinon l'appelant (et son
        // loader/toast de succès) peut s'exécuter avant que l'écriture
        // n'ait réellement abouti.
        await this.apiService.update(`trainings/${currentTraining.id}`, currentTraining, Training.toObject);
      }

      if (relationRole !== 'tutor' || !tutorStillLinked) {
        await this.endRelationAffiliation(currentTraining, user.id);
      }

      return currentTraining;
    } catch (e) {
      throw e;
    }
  }

  /**
   * F-04 (audit du 28/10/2026) : le rôle de chaque relation à détacher est
   * désormais déterminé par l'appartenance RÉELLE dans CETTE formation
   * (`Training.membershipIds`), jamais par `user.role` (légataire global) —
   * même principe que `unlinkUserFromTraining` (S3-A), étendu ici au retrait
   * collectif, qui le contournait encore. Un compte peut tenir plusieurs
   * rôles dans une même formation (ex. un apprenti qui tutore un autre
   * apprenti) : chaque relation réellement détenue est détachée, pas
   * seulement une déduite au hasard.
   *
   * Un tuteur retiré n'est détaché QUE des apprentis EUX-MÊMES présents dans
   * cette sélection (`users`) — jamais de tous ceux qu'il accompagne par
   * ailleurs dans la formation (régression trouvée par `link-up-verifier`,
   * corrigée le 29/10/2026 : une première version détachait trop largement,
   * cassant un apprenti non sélectionné du même tuteur). Déterminés par un
   * instantané pris AVANT toute mutation (pas relus en direct sur
   * `currentTraining.apprentices`, qui peut déjà avoir perdu l'enregistrement
   * d'un apprenti traité plus tôt dans cette même boucle, selon l'ordre de
   * `users`) : retirer l'apprenti X retire déjà le lien X↔tuteur en
   * supprimant l'enregistrement complet de X (branche 'apprentice'
   * ci-dessus) — la branche tuteur ne fait donc que réévaluer son statut
   * (toujours lié ailleurs, ou plus du tout dans cette formation) une fois
   * tous les apprentis visés traités, y compris quand ils ont déjà disparu.
   */
  async deleteTrainingUsers(currentTraining: Training, users: User[]): Promise<Training> {
    try {
      const { apprenticeIds, tutorIds, managerIds } = Training.membershipIds(currentTraining);
      const idsEnCoursDeRetrait = new Set(users.map((u) => u.id));
      const apprentisVisesParTuteur = new Map<string, string[]>();
      for (const tutorId of tutorIds) {
        const ids = currentTraining.apprentices
          .filter((a) => a.tutors?.includes(tutorId) && idsEnCoursDeRetrait.has(a.id))
          .map((a) => a.id);
        if (ids.length > 0) {
          apprentisVisesParTuteur.set(tutorId, ids);
        }
      }

      for (const user of users) {
        if (managerIds.has(user.id)) {
          currentTraining = await this.unlinkUserFromTraining(currentTraining, user, 'manager', null, false);
        }
        if (apprenticeIds.has(user.id)) {
          currentTraining = await this.unlinkUserFromTraining(currentTraining, user, 'apprentice', null, false);
        }
        if (tutorIds.has(user.id)) {
          let auMoinsUnApprentiEncorePresent = false;
          for (const apprenticeId of apprentisVisesParTuteur.get(user.id) ?? []) {
            // L'apprenti visé peut déjà avoir été retiré en entier (branche
            // 'apprentice' ci-dessus, traitée plus tôt dans cette boucle) :
            // rien à détacher pour lui, le statut du tuteur sera réévalué
            // juste en dessous dans tous les cas.
            if (currentTraining.apprentices.some((a) => a.id === apprenticeId)) {
              auMoinsUnApprentiEncorePresent = true;
              currentTraining = await this.unlinkUserFromTraining(
                currentTraining,
                user,
                'tutor',
                { id: apprenticeId } as User,
                false,
              );
            }
          }
          if (!auMoinsUnApprentiEncorePresent) {
            // Tous les apprentis visés sont déjà partis : le tuteur n'a plus
            // rien à détacher explicitement, mais son affiliation doit tout
            // de même se terminer s'il n'accompagne plus personne ICI.
            const tutorStillLinked = currentTraining.apprentices.some((a) => a.tutors?.includes(user.id));
            if (!tutorStillLinked) {
              await this.endRelationAffiliation(currentTraining, user.id);
            }
          }
        }
      }
      console.log('unlink users currentTraining: ', currentTraining);
      await this.apiService.update(`trainings/${currentTraining.id}`, currentTraining, Training.toObject);
      return currentTraining;
    } catch (e) {
      throw e;
    }
  }

  private async endRelationAffiliation(currentTraining: Training, identityId: string): Promise<void> {
    await this.affiliationService.endAffiliationIfExists(identityId, currentTraining.id, new Date().toISOString());
  }

  private async upsertRelationAffiliation(
    currentTraining: Training,
    identityId: string,
    role: AffiliationRole,
  ): Promise<void> {
    await this.affiliationService.upsertAffiliation(
      new Affiliation(
        identityId,
        currentTraining.id,
        currentTraining.trainingCenterId,
        role,
        'active',
        this.userService.getCurrentUser()?.id,
        currentTraining.networkId,
      ),
    );
  }

  async deleteTraining(trainingId: string): Promise<void> {
    try {
      await this.apiService.delete('trainings', trainingId);
    } catch (e) {
      throw e;
    }
  }

  async createOrUpdateTraining(training: Training): Promise<Training> {
    try {
      if (!training?.id || training?.id === '') {
        console.log('create new training: ', training);

        if (await this.trainingExists(training)) {
          throw { code: 'create/training-exists' };
        }

        training.id = await this.apiService.create<Training>('trainings', training, Training.toObject);
      } else {
        await this.apiService.update(`trainings/${training.id}`, training, Training.toObject);
      }

      return training;
    } catch (e) {
      throw e;
    }
  }

  async trainingExists(training: Training): Promise<Training | null> {
    const trainings = await this.apiService.fetchAll<Training>('trainings', Training.fromObject, [
      {
        fieldPath: 'title',
        condition: '==',
        value: training?.title,
      },
    ]);
    return trainings?.length > 0 ? trainings[0] : null;
  }

  /**
   * S3-A : `relationRole` est désormais explicite — le rôle de la relation
   * qu'on crée, pas déduit de `user.role` (global). Un compte dont le rôle
   * actuel diffère (ex. un tuteur qu'on rattache comme stagiaire dans une
   * autre formation) doit pouvoir créer la bonne relation ; le déduire de
   * `user.role` tombait silencieusement dans aucun cas du switch.
   */
  async linkUserToTraining(
    currentTraining: Training,
    user: User,
    relationRole: AffiliationRole,
    linkedUser?: User,
    update = true,
  ): Promise<Training> {
    try {
      let relationCreated = relationRole !== 'tutor';

      switch (relationRole) {
        case 'manager':
          if (!currentTraining.managers.includes(user.id)) {
            currentTraining.managers.push(user.id);
          }
          break;
        case 'apprentice':
          if (currentTraining.apprentices.filter((a) => a.id === user.id).length === 0) {
            currentTraining.apprentices.push({ id: user.id, tutors: [] });
          }
          break;
        case 'tutor':
          if (linkedUser) {
            let apprenticeIndex = currentTraining.apprentices.findIndex(
              (apprentice) => apprentice.id === linkedUser.id,
            );
            if (apprenticeIndex !== -1) {
              if (!currentTraining.apprentices[apprenticeIndex].tutors.includes(user.id)) {
                currentTraining.apprentices[apprenticeIndex].tutors.push(user.id);
              }
              relationCreated = true;
            }
          }
          break;
      }

      if (update) {
        this.apiService.update(`trainings/${currentTraining.id}`, currentTraining, Training.toObject);
      }

      if (relationCreated) {
        await this.upsertRelationAffiliation(currentTraining, user.id, relationRole);
      }

      return currentTraining;
    } catch (e) {
      throw e;
    }
  }

  setCurrentTrainingId(trainingId: string) {
    this.currentTrainingIdSubject.next(trainingId);
  }

  getCurrentTrainingId() {
    return this.currentTrainingIdSubject.getValue();
  }
}
